import React, { Component, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

interface RoutingErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface RoutingErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

class RoutingErrorBoundaryClass extends Component<RoutingErrorBoundaryProps, RoutingErrorBoundaryState> {
  constructor(props: RoutingErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RoutingErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Routing Error Boundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Log routing errors to error tracking service
    if (import.meta.env.PROD) {
      // Example: Send to error tracking service
      // errorTrackingService.captureException(error, { 
      //   extra: { 
      //     ...errorInfo,
      //     route: window.location.pathname,
      //     userAgent: navigator.userAgent
      //   } 
      // });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <RoutingErrorFallback error={this.state.error} onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

interface RoutingErrorFallbackProps {
  error?: Error;
  onRetry: () => void;
}

function RoutingErrorFallback({ error, onRetry }: RoutingErrorFallbackProps) {
  const location = useLocation();
  
  const isChunkLoadError = error?.message?.includes('Loading chunk') || 
                          error?.message?.includes('ChunkLoadError') ||
                          error?.name === 'ChunkLoadError';

  const isNetworkError = error?.message?.includes('NetworkError') ||
                        error?.message?.includes('Failed to fetch');

  const getErrorType = () => {
    if (isChunkLoadError) {
      return {
        title: 'Page Loading Error',
        description: 'There was an issue loading this page. This usually happens after an app update.',
        icon: (
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        ),
        primaryAction: 'Refresh Page',
        primaryActionFn: () => window.location.reload(),
      };
    }

    if (isNetworkError) {
      return {
        title: 'Connection Error',
        description: 'Unable to load the page due to a network issue. Please check your internet connection.',
        icon: (
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728" />
          </svg>
        ),
        primaryAction: 'Try Again',
        primaryActionFn: onRetry,
      };
    }

    return {
      title: 'Navigation Error',
      description: 'Something went wrong while navigating to this page.',
      icon: (
        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      primaryAction: 'Try Again',
      primaryActionFn: onRetry,
    };
  };

  const errorType = getErrorType();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div 
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div 
          className="bg-white rounded-lg shadow-lg p-8"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          {/* Error Icon */}
          <motion.div 
            className="w-16 h-16 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3, type: "spring" }}
          >
            {errorType.icon}
          </motion.div>
          
          <motion.h1 
            className="text-2xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {errorType.title}
          </motion.h1>
          
          <motion.p 
            className="text-gray-600 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {errorType.description}
          </motion.p>

          {/* Current path info */}
          <motion.div 
            className="mb-6 p-3 bg-gray-50 rounded-md text-sm text-gray-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <strong>Current path:</strong> {location.pathname}
          </motion.div>
          
          {/* Development error details */}
          {import.meta.env.DEV && error && (
            <motion.details 
              className="mb-6 text-left"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 mb-2">
                Show error details (development only)
              </summary>
              <div className="p-3 bg-red-50 rounded-md border border-red-200">
                <p className="text-xs text-red-700 font-mono break-all">
                  {error.message}
                </p>
              </div>
            </motion.details>
          )}
          
          {/* Action buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-3 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <motion.button
              onClick={errorType.primaryActionFn}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {errorType.primaryAction}
            </motion.button>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/"
                className="inline-block bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Go Home
              </Link>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/login"
                className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Login
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Additional help */}
        <motion.div 
          className="mt-6 text-center text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <p className="text-sm">
            If this problem persists, try clearing your browser cache or contact support.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

// Wrapper component that provides router context
export const RoutingErrorBoundary: React.FC<RoutingErrorBoundaryProps> = (props) => {
  return <RoutingErrorBoundaryClass {...props} />;
};

export default RoutingErrorBoundary;